import type { SitemapUrl } from "../config/sitemapUrls";
import type { ContentPage, ContentSection } from "./types";
import howToTrainAiVoiceModel from "./blog/how-to-train-an-ai-voice-model";
import bestAiCoverGenerators from "./compare/best-ai-cover-generators";

// Every blog and comparison page. A page that isn't here doesn't exist: no
// route, no sitemap entry, no Markdown copy.
export const CONTENT: ContentPage[] = [
  howToTrainAiVoiceModel,
  bestAiCoverGenerators,
];

export function contentPath(page: Pick<ContentPage, "section" | "slug">) {
  return `/${page.section}/${page.slug}`;
}

export function getContent(section: ContentSection, slug: string) {
  return CONTENT.find((p) => p.section === section && p.slug === slug);
}

// Every Markdown string on a page, in render order: md blocks, then each
// step body where a steps block sits. getStaticProps serializes them in this
// order and ContentArticle consumes them in the same order.
export function mdSources(page: ContentPage): string[] {
  return page.blocks.flatMap((b) =>
    b.type === "md"
      ? [b.text]
      : b.type === "steps"
        ? b.items.map((i) => i.body)
        : [],
  );
}

// Words / 220 a minute, at least 1.
export function readingMinutes(page: ContentPage): number {
  const picks = page.blocks.flatMap((b) =>
    b.type === "picks" ? b.items.map((p) => p.body) : [],
  );
  const text = [page.answer, ...mdSources(page), ...picks].join(" ");
  return Math.max(1, Math.round(text.split(/\s+/).length / 220));
}

// Newest first.
export function listContent(section: ContentSection) {
  return CONTENT.filter((p) => p.section === section).sort((a, b) =>
    b.published.localeCompare(a.published),
  );
}

// Unlike the voice URLs these have a real lastmod: the page's `updated` date.
export const contentSitemapUrls: (SitemapUrl & { lastmod?: Date })[] = [
  { loc: "/blog", priority: 0.7, changefreq: "weekly" },
  { loc: "/compare", priority: 0.7, changefreq: "weekly" },
  ...CONTENT.map((p) => ({
    loc: contentPath(p),
    priority: 0.8,
    changefreq: "monthly" as const,
    lastmod: new Date(`${p.updated}T00:00:00Z`),
  })),
];
